function initCasekooSlider(root) {
  if (root.dataset.ckReady === 'true') return;
  root.dataset.ckReady = 'true';

  const slides = [...root.querySelectorAll('[data-ck-slide]')];
  const dots = [...root.querySelectorAll('[data-ck-dot]')];
  const previous = root.querySelector('[data-ck-previous]');
  const next = root.querySelector('[data-ck-next]');
  const viewport = root.querySelector('.ck-slider__viewport');
  const autoplaySeconds = Number(root.dataset.autoplay || 0);
  const autoplayVideoSlides = root.dataset.autoplayVideoSlides !== 'false';
  let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
  let timer;

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const isVideoForCurrentDevice = (video) => {
    if (video.classList.contains('ck-media__video--desktop')) return !isMobile();
    if (video.classList.contains('ck-media__video--mobile')) return isMobile();
    return true;
  };
  const loadDeferredVideo = (video) => {
    if (!video || video.dataset.ckVideoLoaded === 'true') return;

    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      video.removeAttribute('data-poster');
    }

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.ckVideoLoaded = 'true';
    video.load();
  };
  const activeVideo = () => {
    const activeSlide = slides[activeIndex];
    return [...(activeSlide?.querySelectorAll('video') || [])].find(isVideoForCurrentDevice);
  };
  const nextAutoplayIndex = () => {
    if (autoplayVideoSlides) return activeIndex + 1;

    for (let offset = 1; offset <= slides.length; offset += 1) {
      const nextIndex = (activeIndex + offset) % slides.length;
      if (!slides[nextIndex].querySelector('video')) return nextIndex;
    }

    return activeIndex;
  };
  const syncVideos = () => {
    slides.forEach((slide, slideIndex) => {
      slide.querySelectorAll('video').forEach((video) => {
        const shouldPlay = slideIndex === activeIndex && isVideoForCurrentDevice(video) && !document.hidden;

        video.muted = true;
        if (shouldPlay) {
          loadDeferredVideo(video);
          const playAttempt = video.play();
          if (playAttempt && typeof playAttempt.catch === 'function') playAttempt.catch(() => {});
          return;
        }

        video.pause();
        if (slideIndex !== activeIndex && video.currentTime > 0) video.currentTime = 0;
      });
    });
  };

  const show = (index) => {
    activeIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
    window.requestAnimationFrame(syncVideos);
  };

  const stop = () => window.clearTimeout(timer);
  const start = () => {
    stop();
    if (autoplaySeconds > 0 && slides.length > 1) {
      const video = activeVideo();
      let delay = autoplaySeconds * 1000;

      if (video) {
        if (Number.isFinite(video.duration) && video.duration > 0) {
          delay = Math.max(delay, video.duration * 1000);
        } else {
          video.addEventListener('loadedmetadata', start, { once: true });
        }
      }

      timer = window.setTimeout(() => {
        show(nextAutoplayIndex());
        start();
      }, delay);
    }
  };

  previous?.addEventListener('click', () => {
    show(activeIndex - 1);
    start();
  });
  next?.addEventListener('click', () => {
    show(activeIndex + 1);
    start();
  });
  dots.forEach((dot, dotIndex) => {
    dot.addEventListener('click', () => {
      show(dotIndex);
      start();
    });
  });

  if (viewport && root.classList.contains('ck-slider--anniversary-home') && slides.length > 1) {
    let swipeActive = false;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerDeltaX = 0;
    let pointerDeltaY = 0;
    let lastTouchTime = 0;
    let suppressSlideClick = false;
    let suppressSlideClickTimer;

    const resetSwipe = () => {
      swipeActive = false;
      pointerDeltaX = 0;
      pointerDeltaY = 0;
    };

    const beginSwipe = (clientX, clientY) => {
      if (!isMobile()) return;
      swipeActive = true;
      pointerStartX = clientX;
      pointerStartY = clientY;
      pointerDeltaX = 0;
      pointerDeltaY = 0;
      stop();
    };

    const moveSwipe = (clientX, clientY) => {
      if (!swipeActive) return false;
      pointerDeltaX = clientX - pointerStartX;
      pointerDeltaY = clientY - pointerStartY;
      return Math.abs(pointerDeltaX) > 8 && Math.abs(pointerDeltaX) > Math.abs(pointerDeltaY);
    };

    const finishSwipe = (allowSlideChange = true) => {
      if (!swipeActive) return;

      const swipeDistance = Math.min(80, Math.max(44, viewport.clientWidth * 0.12));
      const isHorizontalSwipe =
        allowSlideChange &&
        Math.abs(pointerDeltaX) >= swipeDistance &&
        Math.abs(pointerDeltaX) > Math.abs(pointerDeltaY) * 1.15;

      if (isHorizontalSwipe) {
        suppressSlideClick = true;
        window.clearTimeout(suppressSlideClickTimer);
        suppressSlideClickTimer = window.setTimeout(() => {
          suppressSlideClick = false;
        }, 500);
        show(activeIndex + (pointerDeltaX < 0 ? 1 : -1));
      }

      resetSwipe();
      start();
    };

    if ('PointerEvent' in window) {
      let pointerId;
      viewport.addEventListener('pointerdown', (event) => {
        if (!isMobile() || event.isPrimary === false || event.button !== 0) return;
        pointerId = event.pointerId;
        viewport.setPointerCapture?.(pointerId);
        beginSwipe(event.clientX, event.clientY);
      });
      viewport.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        if (moveSwipe(event.clientX, event.clientY)) event.preventDefault();
      });
      viewport.addEventListener('pointerup', (event) => {
        if (event.pointerId !== pointerId) return;
        if (viewport.hasPointerCapture?.(pointerId)) viewport.releasePointerCapture(pointerId);
        pointerId = undefined;
        finishSwipe();
      });
      viewport.addEventListener('pointercancel', (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = undefined;
        finishSwipe(false);
      });
    } else {
      viewport.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) return;
          lastTouchTime = Date.now();
          beginSwipe(event.touches[0].clientX, event.touches[0].clientY);
        },
        { passive: true }
      );
      viewport.addEventListener(
        'touchmove',
        (event) => {
          if (event.touches.length !== 1) return;
          if (moveSwipe(event.touches[0].clientX, event.touches[0].clientY)) event.preventDefault();
        },
        { passive: false }
      );
      viewport.addEventListener('touchend', () => finishSwipe(), { passive: true });
      viewport.addEventListener('touchcancel', () => finishSwipe(false), { passive: true });

      viewport.addEventListener('mousedown', (event) => {
        if (event.button !== 0 || Date.now() - lastTouchTime < 700) return;
        beginSwipe(event.clientX, event.clientY);
      });
      viewport.addEventListener('mousemove', (event) => {
        if (moveSwipe(event.clientX, event.clientY)) event.preventDefault();
      });
      viewport.addEventListener('mouseup', () => finishSwipe());
      viewport.addEventListener('mouseleave', () => finishSwipe(false));
    }

    viewport.addEventListener(
      'click',
      (event) => {
        if (!suppressSlideClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressSlideClick = false;
      },
      true
    );
  }

  root.addEventListener('mouseenter', stop);
  root.addEventListener('mouseleave', start);
  root.addEventListener('focusin', stop);
  root.addEventListener('focusout', start);
  window.addEventListener('resize', syncVideos, { passive: true });
  document.addEventListener('visibilitychange', syncVideos);
  show(activeIndex);
  start();
}

function moveCasekooScrollTrack(root, direction) {
  const track = root.querySelector('[data-ck-scroll-track]');
  if (!track) return;

  const card = [...track.children].find((child) => child instanceof HTMLElement && !child.hidden);
  const computedStyle = window.getComputedStyle(track);
  let gap = Number.parseFloat(computedStyle.columnGap || computedStyle.gap || '0');
  if (!Number.isFinite(gap)) gap = 0;
  const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;

  track.scrollTo({
    left: Math.max(0, track.scrollLeft + direction * distance),
    behavior: 'smooth',
  });
  window.setTimeout(() => root.dispatchEvent(new CustomEvent('ck:scroll:update')), 360);
}

function initCasekooScrollControlFallback() {
  window.casekooScrollControl = (control, direction) => {
    const root = control.closest('[data-ck-scroll]');
    if (!root) return;
    moveCasekooScrollTrack(root, direction);
  };

  if (document.documentElement.dataset.ckScrollFallbackReady === 'true') return;
  document.documentElement.dataset.ckScrollFallbackReady = 'true';

  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-ck-scroll-previous], [data-ck-scroll-next]');
    if (!control || control.disabled) return;
    if (control.dataset.ckDirectScroll === 'true') return;

    const root = control.closest('[data-ck-scroll]');
    if (!root) return;

    const direction = control.matches('[data-ck-scroll-previous]') ? -1 : 1;
    moveCasekooScrollTrack(root, direction);
  });
}

function initCasekooScroll(root) {
  if (root.dataset.ckScrollReady === 'true') return;
  root.dataset.ckScrollReady = 'true';
  const track = root.querySelector('[data-ck-scroll-track]');
  const previous = root.querySelector('[data-ck-scroll-previous]');
  const next = root.querySelector('[data-ck-scroll-next]');
  if (!track) return;
  const autoScrollDirection = Number(root.dataset.ckAutoScroll || 0);
  let autoScrollFrame;
  let autoScrollLastFrame;

  const updateControls = () => {
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (previous) previous.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
  };

  const stopAutoScroll = () => {
    window.cancelAnimationFrame(autoScrollFrame);
    autoScrollFrame = undefined;
    autoScrollLastFrame = undefined;
  };

  const runAutoScroll = (timestamp) => {
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

    if (maxScroll > 1) {
      if (!autoScrollLastFrame) autoScrollLastFrame = timestamp;
      const elapsed = timestamp - autoScrollLastFrame;
      autoScrollLastFrame = timestamp;
      const distance = (elapsed / 1000) * 24 * autoScrollDirection;
      let nextLeft = track.scrollLeft + distance;

      if (nextLeft >= maxScroll - 1) {
        nextLeft = 0;
      } else if (nextLeft <= 1 && autoScrollDirection < 0) {
        nextLeft = maxScroll;
      }

      track.scrollLeft = nextLeft;
    }

    autoScrollFrame = window.requestAnimationFrame(runAutoScroll);
  };

  const startAutoScroll = () => {
    stopAutoScroll();
    if (autoScrollDirection !== 0) {
      autoScrollFrame = window.requestAnimationFrame(runAutoScroll);
    }
  };

  const move = (direction) => {
    stopAutoScroll();
    moveCasekooScrollTrack(root, direction);
    window.setTimeout(updateControls, 360);
    window.setTimeout(startAutoScroll, 900);
  };

  previous?.addEventListener('click', (event) => {
    event.stopPropagation();
    if (event.currentTarget.dataset.ckDirectScroll === 'true') return;
    move(-1);
  });
  next?.addEventListener('click', (event) => {
    event.stopPropagation();
    if (event.currentTarget.dataset.ckDirectScroll === 'true') return;
    move(1);
  });
  track.addEventListener('scroll', updateControls);
  root.addEventListener('ck:scroll:update', updateControls);
  root.addEventListener('mouseenter', stopAutoScroll);
  root.addEventListener('mouseleave', startAutoScroll);
  root.addEventListener('focusin', stopAutoScroll);
  root.addEventListener('focusout', startAutoScroll);
  updateControls();
  startAutoScroll();
}

function initCasekooTabs(root) {
  if (root.dataset.ckTabsReady === 'true') return;
  root.dataset.ckTabsReady = 'true';
  const tabs = [...root.querySelectorAll('[data-ck-tab]')];
  const cards = [...root.querySelectorAll('[data-ck-category]')];

  const showCategory = (category) => {
    tabs.forEach((tab) => {
      const isActive = tab.dataset.ckTab === category;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });
    cards.forEach((card) => {
      card.hidden = card.dataset.ckCategory !== category;
    });
    root.querySelector('[data-ck-scroll-track]')?.scrollTo({ left: 0, behavior: 'auto' });
    root.dispatchEvent(new CustomEvent('ck:scroll:update'));
  };

  tabs.forEach((tab) => tab.addEventListener('click', () => showCategory(tab.dataset.ckTab)));
  if (tabs[0]) showCategory(tabs[0].dataset.ckTab);
}

function initCasekooSections(scope = document) {
  initCasekooScrollControlFallback();
  scope.querySelectorAll('[data-ck-slider]').forEach(initCasekooSlider);
  scope.querySelectorAll('[data-ck-scroll]').forEach(initCasekooScroll);
  scope.querySelectorAll('[data-ck-tabs]').forEach(initCasekooTabs);
  processCasekooEmbeds(scope);
}

function processCasekooEmbeds(scope = document) {
  if (!scope.querySelector('.instagram-media')) return;

  const process = () => window.instgrm?.Embeds?.process?.();

  if (window.instgrm?.Embeds?.process) {
    process();
    return;
  }

  if (!document.querySelector('script[src*="instagram.com/embed.js"]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.instagram.com/embed.js';
    script.addEventListener('load', process);
    document.head.appendChild(script);
  }
}

document.addEventListener('DOMContentLoaded', () => initCasekooSections());
document.addEventListener('shopify:section:load', (event) => initCasekooSections(event.target));
